import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

const baseLinks = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
];
const tailLinks = [
  { to: "/about", label: "Heritage" },
  { to: "/track", label: "Track" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const { count } = useCart();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [promo, setPromo] = React.useState(null);
  const [categories, setCategories] = React.useState([]);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/config").then((r) => { if (r.data.promo_enabled && r.data.promo_text) setPromo(r.data.promo_text); else setPromo(null); }).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [loc]);

  useEffect(() => { setOpen(false); }, [loc]);

  const links = [...baseLinks, ...categories.map((c) => ({ to: `/shop?category=${c.slug}`, label: c.name })), ...tailLinks];

  return (
    <div className="fixed top-0 inset-x-0 z-50">
      {promo && (
        <div className="bg-[hsl(var(--primary))] text-white text-center py-2 px-4 overline animate-pulse" data-testid="promo-bar">
          {promo}
        </div>
      )}
      <header
        data-testid="site-header"
        className={`transition-all duration-500 ${scrolled ? "bg-[hsl(38_27%_97%/0.82)] backdrop-blur-xl border-b border-border" : "bg-transparent"}`}
      >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src="/logo.png?v=2" alt="Navrang Udaipur" className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-2xl md:text-3xl font-600 tracking-tight text-foreground">Navrang</span>
            <span className="overline text-[hsl(var(--primary))] -mt-1">Udaipur</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              className="font-body text-sm text-foreground/80 hover:text-foreground link-underline transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <Link to="/cart" data-testid="cart-link" className="relative group">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[hsl(var(--primary))] text-white text-[10px] font-mono-label w-4 h-4 flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </Link>
          <button data-testid="menu-toggle" className="lg:hidden" onClick={() => setOpen((v) => !v)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[hsl(38_27%_97%/0.98)] backdrop-blur-xl border-b border-border">
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <Link key={l.label} to={l.to} className="font-display text-2xl text-foreground">{l.label}</Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
      </header>
    </div>
  );
};
